import {Poi} from '../../react-app-env';
import React, {ReactNode} from 'react';
import ListViewGroup from '../listview/listviewgroup/ListViewGroup';
import {i18n} from '../../i18n/i18n';
import ListViewItem from '../listview/listviewitem/ListViewItem';
import {noop} from '../../utils/Utils';
import {DefaultItemDecorations} from '../listview/listviewitem/ListViewItem.types';
import {PoiMetaData} from '../../services/MetaDataService';
import {StackApi} from '../stack/Stack.types';
import OpeningHoursSidebarPage from './openinghours/OpeningHoursSidebarPage';
import {useReadonly} from '../context/ReadonlyContext';

export interface ScheduleGroupProps {
  readonly poi: Poi;
  readonly metaData: PoiMetaData | null;
  readonly stackApi: StackApi;
}

function ScheduleGroup ({ stackApi, poi, metaData }: ScheduleGroupProps) {
  const readonly = useReadonly();

  function renderOpeningHours(): ReactNode {
    if (metaData?.openingHours.alwaysOpen) {
      return <ListViewItem key="opening-hours"
                           title={i18n().poiDetails.schedule.alwaysOpen}
                           iconCategory="fa-clock fa-regular"/>;
    }

    if (metaData?.openingHours.permanentlyClosed) {
      return <ListViewItem key="opening-hours"
                           title={i18n().poiDetails.schedule.permanentlyClosed}
                           iconCategory="fa-clock fa-regular"/>;
    }

    if (metaData?.openingHours.temporarilyClosed) {
      return <ListViewItem key="opening-hours"
                           title={i18n().poiDetails.schedule.temporarilyClosed}
                           iconCategory="fa-clock fa-regular"/>;
    }

    if (metaData?.openingHours.openNow === undefined) {
      return null;
    }

    const label = metaData?.openingHours.openNow
        ? i18n().poiDetails.schedule.currentlyOpen
        : i18n().poiDetails.schedule.currentlyClosed;

    const decoration = metaData?.openingHours.details
        ? DefaultItemDecorations.CHEVRON
        : undefined;

    const onClick = metaData?.openingHours.details
        ? () => stackApi.push({
          id:   'opening-hours--' + poi.id,
          node: <OpeningHoursSidebarPage
                    poi={poi}
                    metaData={metaData}
                    stackApi={stackApi}/>
        })
        : noop;

    return <ListViewItem key="opening-hours"
                         title={label}
                         iconCategory="fa-clock fa-regular"
                         decoration={decoration}
                         onClick={onClick}/>;
  }

  const listItems = [];
  if (readonly) {
    poi.transit && listItems.push(
        <ListViewItem key="transit"
                      title={poi.transit}
                      iconCategory="fa-arrows-split-up-and-left"/>
    );
    poi.duration && listItems.push(
        <ListViewItem key="duration"
                      title={poi.duration || i18n().poiDetails.schedule.describeDuration}
                      iconCategory="fa-hourglass-half fa-regular"/>
    );
  } else {
    listItems.push(
        <ListViewItem key="edit-transit"
                      title={poi.transit || i18n().poiDetails.schedule.describeTransit}
                      iconCategory="fa-arrows-split-up-and-left"
                      decoration={DefaultItemDecorations.EDIT}
                      onClick={() => noop()}/>,
        <ListViewItem key="edit-duration"
                      title={poi.duration || i18n().poiDetails.schedule.describeDuration}
                      iconCategory="fa-hourglass-half fa-regular"
                      decoration={DefaultItemDecorations.EDIT}
                      onClick={() => noop()}/>
    );
  }

  const openingHoursListItem = renderOpeningHours();
  if (openingHoursListItem) {
    listItems.push(openingHoursListItem);
  }

  return listItems.length === 0 ? null : (
      <ListViewGroup>
        {listItems}
      </ListViewGroup>
  );
}

export default ScheduleGroup;
